import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { guidePageSchema } from '../../utils/schema';

const ARTICLE_SCHEMA = {
  '@type': 'Article',
  headline: 'Can You Complete All Arizona Real Estate CE Online? Yes.',
  description: 'All 24 Arizona CE hours can be done online — no classroom required. How online ADRE-approved CE works and what to look for.',
  datePublished: '2026-06-01',
  dateModified: '2026-06-10',
  author: { '@type': 'Organization', name: 'Steady Education' },
  publisher: {
    '@type': 'EducationalOrganization',
    name: 'Steady Education',
    url: 'https://steadyeducation.com',
  },
};

const RELATED = [
  { url: '/arizona-ce-provider-comparison', title: 'Best Arizona CE Providers (Compared)', desc: 'Every major ADRE-approved provider for 2026, online vs classroom.' },
  { url: '/arizona-real-estate-ce-requirements', title: 'Arizona CE Requirements 2026', desc: 'The complete 24-hour breakdown by category.' },
  { url: '/how-fast-can-i-finish-arizona-ce', title: 'How Fast Can You Finish 24 Hours?', desc: 'The 9-hour daily cap and the minimum 3-day schedule.' },
  { url: '/arizona-ce-2025-rule-changes', title: '2025 Rule Changes', desc: 'The new Water, Firewise, and Deed Fraud mandates.' },
];

export default function OnlineCePage() {
  useSEO({
    title: 'Can You Complete All Arizona Real Estate CE Online? Yes.',
    description: 'All 24 Arizona CE hours can be done online — no classroom required. How online ADRE-approved CE works and what to look for.',
    url: canonicalUrl('/arizona-ce-online'),
    jsonLd: guidePageSchema(ARTICLE_SCHEMA, {
      title: 'Arizona CE Online',
      url: canonicalUrl('/arizona-ce-online'),
    }),
  });

  return (
    <ResourceLayout
      breadcrumb="Online CE"
      title="Can You Complete All 24 Arizona CE Hours Online? (Yes — Here's How It Works)"
      subtitle="All 24 of your required CE hours can be completed online. No classroom attendance, no scheduled Zoom sessions, no exceptions needed. ADRE approves online self-paced courses the same way it approves classroom courses — what matters is that the school and course are ADRE-approved, not the delivery format."
      relatedGuides={RELATED}
    >
      <h2>How ADRE-approved online CE actually works</h2>
      <ul>
        <li>You work through the course material at your own pace, on any device. Progress saves automatically.</li>
        <li>Each course ends with an exam — <strong>70% or higher passes</strong>, and retakes are available.</li>
        <li>Your certificate is issued on passing, with the course number and dates ADRE requires.</li>
        <li>You submit each certificate through your ADRE Licensee Login (online CE or classroom — this step is always yours).</li>
      </ul>

      <h2>Online vs. classroom in Arizona — honest tradeoffs</h2>
      <ul>
        <li><strong>Online self-paced:</strong> cheapest, fastest, fits around showings and closings. Start at 10 PM if that's your life. Best for: nearly everyone renewing on a budget or a deadline.</li>
        <li><strong>Livestream classes:</strong> fixed schedule, live instructor Q&A. Best for: agents who want interaction and structure.</li>
        <li><strong>In-person classroom:</strong> networking value, full days. Best for: agents who treat CE as an event. Costs the most in both dollars and time.</li>
      </ul>

      <p>The certificate is identical in ADRE's eyes — <strong>pay for the format you'll actually finish.</strong></p>

      <h2>One rule the format doesn't change</h2>
      <p>The <strong>9-hour daily cap</strong> applies online too. Even self-paced, 24 hours takes a minimum of three days.</p>

      <div style={inlineCta}>
        <strong>Complete Arizona CE Package — 100% online, $49.99.</strong> All 24 ADRE-required hours including the 2025 mandates.
        <div style={{ marginTop: 14 }}>
          <Link to="/arizona/courses" style={inlineCtaBtn}>See the package →</Link>
        </div>
      </div>
    </ResourceLayout>
  );
}

const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
